"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ApiError = void 0;
exports.withQuery = withQuery;
exports.createApiClient = createApiClient;
function withQuery(path, query) {
    if (!query)
        return path;
    const params = Object.entries(query)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
    if (params.length === 0)
        return path;
    return `${path}${path.includes('?') ? '&' : '?'}${params.join('&')}`;
}
class ApiError extends Error {
    status;
    code;
    details;
    constructor(status, code, message, details) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        this.details = details;
    }
}
exports.ApiError = ApiError;
const AUTH_PATH = /^\/auth\//;
function createApiClient(config) {
    let refreshing = null;
    function refreshOnce() {
        const refresh = config.refreshAccessToken;
        if (!refresh)
            return Promise.resolve(null);
        refreshing ??= refresh()
            .catch(() => null)
            .finally(() => {
            refreshing = null;
        });
        return refreshing;
    }
    async function request(method, path, args = {}, retried = false) {
        const headers = new Headers(config.headers);
        new Headers(args.headers).forEach((value, key) => headers.set(key, value));
        const token = config.getAccessToken();
        if (token)
            headers.set('authorization', `Bearer ${token}`);
        if (args.body !== undefined)
            headers.set('content-type', 'application/json');
        if (args.blob)
            headers.set('content-type', args.blob.type || 'application/octet-stream');
        let res;
        try {
            res = await fetch(`${config.baseUrl}${withQuery(path, args.query)}`, {
                method,
                headers,
                body: args.blob ?? (args.body === undefined ? undefined : JSON.stringify(args.body)),
                credentials: 'include',
                signal: args.signal,
            });
        }
        catch (e) {
            if (!args.signal?.aborted)
                config.onUnavailable?.();
            throw e;
        }
        if (res.status === 502 || res.status === 503 || res.status === 504)
            config.onUnavailable?.();
        if (res.status === 401 && !retried && token && config.refreshAccessToken && !AUTH_PATH.test(path)) {
            if (await refreshOnce())
                return request(method, path, args, true);
        }
        if (res.status === 401 && !AUTH_PATH.test(path)) {
            config.onAuthFailure?.();
        }
        if (!res.ok) {
            let payload = null;
            try {
                payload = (await res.json());
            }
            catch {
                payload = null;
            }
            if (res.status === 403 && payload?.code === 'user_blocked')
                config.onBlocked?.();
            throw new ApiError(res.status, payload?.code ?? 'unknown', payload?.message ?? res.statusText, payload?.details);
        }
        if (res.status === 204)
            return undefined;
        const text = await res.text();
        return (text.length === 0 ? null : JSON.parse(text));
    }
    return {
        get: (path, args) => request('GET', path, args),
        post: (path, body, args) => request('POST', path, { ...args, body }),
        put: (path, body, args) => request('PUT', path, { ...args, body }),
        patch: (path, body, args) => request('PATCH', path, { ...args, body }),
        delete: (path, args) => request('DELETE', path, args),
        upload: (path, blob, args) => request('POST', path, { ...args, blob }),
        putBlob: (path, blob, args) => request('PUT', path, { ...args, blob }),
    };
}
//# sourceMappingURL=client.js.map